import moment from 'moment';
import themeImgPlaceholder from 'assets/images/empty-state-theme-icon.svg';
import constants from 'client/constants';

const nLevelHierarchy: Record<string, number> = {
  SUBTOPIC: 3,
  THEME: 1,
  TOPIC: 2
};

/**
 * The base guide level extensions are mixed (via spread) into the augmented
 * GuideLevel / AuthoringGuideLevel models, so `this` is the host model. We
 * describe the host model methods these extensions rely on as a `this` type so
 * the calls type-check without weakening them. The nested meta/parent/question
 * set values are themselves dynamic JSON:API models, so they are typed with the
 * narrow surface each call site uses.
 */
interface BaseGuideLevelMeta {
  getCountOfQuestions(): number;
  getStudentCountOfUniqueGuesses(): number;
  getStudentAccuracy(): number;
  getStudentCountOfGuesses(): number;
  getStudentCountOfCorrectGuesses(): number;
  getStudentSumOfGuessesTimeElapsed(): number;
}

interface BaseGuideLevelQuestionSet {
  getQuestions(): {size: number};
}

interface BaseGuideLevelParent {
  isEmpty(): boolean;
  get(key: string): unknown;
}

interface BaseGuideLevelHost {
  getNlevel(): number;
  cache(key: string, compute: () => string): string;
  get(key: string): unknown;
  getParent(): BaseGuideLevelParent | null;
  getMeta(): BaseGuideLevelMeta;
  getQuestionSets(): BaseGuideLevelQuestionSet[];
}

export const baseGuideLevelExtensions = {
  getGuideLevelType(this: BaseGuideLevelHost): string {
    // nLevel is always one of the hierarchy values, so `find` always matches.
    return Object.keys(nLevelHierarchy)
      .find((key) => {
        return nLevelHierarchy[key] === this.getNlevel();
      })!
      .toLowerCase();
  },

  isTheme(this: BaseGuideLevelHost): boolean {
    return this.getNlevel() === nLevelHierarchy.THEME;
  },

  isTopic(this: BaseGuideLevelHost): boolean {
    return this.getNlevel() === nLevelHierarchy.TOPIC;
  },

  isSubtopic(this: BaseGuideLevelHost): boolean {
    return this.getNlevel() === nLevelHierarchy.SUBTOPIC;
  },

  getThemeImg(this: BaseGuideLevelHost): string {
    return this.cache('theme_img', () => {
      const path =
        this.get('theme_img') ||
        (this.getParent() !== null &&
          !this.getParent()!.isEmpty() &&
          this.getParent()!.get('theme_img'));
      if (!path) {
        /**
         * The placeholder will be local to the release (`publicPath`)
         */
        return themeImgPlaceholder;
      }
      return constants.SUPPLEMENTS_ORIGIN + '/' + (path as string);
    });
  },

  getMetaCountOfQuestions(this: BaseGuideLevelHost): number {
    return this.getMeta().getCountOfQuestions();
  },

  /**
   * Introspects the returned payload to count questions for Universal Search. Existing metadata functions, such
   * as getMetaCountOfQuestions do not take into account filters, and therefore are not usable for this feature.
   */
  getCountOfReturnedQuestions(this: BaseGuideLevelHost): number {
    return this.getQuestionSets().reduce(
      (count, questionSet) => count + questionSet.getQuestions().size,
      0
    );
  },

  getMetaCountOfUniqueQuestionsAnswered(this: BaseGuideLevelHost): number {
    return this.getMeta().getStudentCountOfUniqueGuesses();
  },

  getMetaStudentAccuracy(this: BaseGuideLevelHost): number {
    return this.getMeta().getStudentAccuracy();
  },

  getMetaStudentCountOfGuesses(this: BaseGuideLevelHost): number {
    return this.getMeta().getStudentCountOfGuesses();
  },

  getMetaStudentCountOfCorrectGuesses(this: BaseGuideLevelHost): number {
    return this.getMeta().getStudentCountOfCorrectGuesses();
  },

  getMetaTimeSpentDuration(this: BaseGuideLevelHost): moment.Duration {
    return moment.duration(this.getMeta().getStudentSumOfGuessesTimeElapsed());
  },

  hasQuestions(this: BaseGuideLevelHost): boolean {
    return this.getMeta().getCountOfQuestions() > 0;
  }
};
